/* 
  FIXED PROJECT BUILDER
  - Prevents Business Unit from reverting to the first list item during edit
  - Ensures auto-selection only occurs for new projects
*/
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Step1Tasks } from './Step1Tasks';
import { Step2Workflows } from './Step2Workflows';
import { Step3Projects } from './Step3Projects';
import { Step4Review } from './Step4Review';
import SafeIcon from '../../common/SafeIcon';
import { useStore } from '../../store/StoreContext';
import { generateId } from '../../utils/helpers';

const STEPS = [
  { id: 1, title: 'Tasks', icon: 'CheckSquare' },
  { id: 2, title: 'Workflows', icon: 'GitMerge' },
  { id: 3, title: 'Compose', icon: 'Layout' },
  { id: 4, title: 'Review', icon: 'ShieldCheck' }
];

export const ProjectBuilder = () => {
  const { projectId } = useParams();
  const { state, addEntity, updateEntity } = useStore();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const loadedProjectId = useRef(null);

  const [tempProject, setTempProject] = useState({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    deadline: '', 
    businessUnitId: '',
    color: '#6C63FF'
  });
  
  const [tempWfs, setTempWfs] = useState([]);

  // EFFECT 1: Load existing project data
  useEffect(() => {
    const p = projectId ? state.projects[projectId] : null;
    
    if (p && loadedProjectId.current !== projectId) {
      setTempProject({
        name: p.name || '',
        description: p.description || '',
        startDate: p.startDate || p.start_date || new Date().toISOString().split('T')[0],
        deadline: p.deadline || '',
        businessUnitId: p.businessUnitId || '', // Explicitly load from DB
        color: p.color || '#6C63FF'
      });
      setTempWfs([...(p.workflows || [])]);
      loadedProjectId.current = projectId;
      
      // Skip setup steps when editing
      if (currentStep === 1) setCurrentStep(3);
    }
  }, [projectId, state.projects]);

  // EFFECT 2: Auto-select first Business Unit ONLY for NEW projects
  useEffect(() => {
    // CRITICAL FIX: Only auto-assign if there's NO projectId (it's a new project)
    // AND if the businessUnitId hasn't been set yet.
    if (!projectId && !tempProject.businessUnitId) {
      const units = Object.values(state.businessUnits || {});
      if (units.length > 0) {
        setTempProject(prev => ({ ...prev, businessUnitId: units[0].id }));
      }
    }
  }, [state.businessUnits, tempProject.businessUnitId, projectId]);

  const handleFinalSave = async () => {
    const projectData = {
      ...tempProject,
      id: projectId || generateId('p'),
      status: 'active',
      workflows: tempWfs
    };
    try {
      if (projectId) {
        await updateEntity('projects', projectData);
      } else {
        await addEntity('projects', projectData);
      }
      navigate('/library/projects');
    } catch (err) {
      alert("Error saving project: " + err.message);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col bg-base-900">
      <div className="p-6 border-b border-base-700 bg-base-800 shrink-0">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">
              {projectId ? `Edit Project: ${tempProject.name || '...'}` : 'Project Builder'}
            </h1>
            <p className="text-sm text-gray-400">Step {currentStep} of 4: {STEPS[currentStep - 1].title}</p>
          </div>
          <div className="flex gap-2">
            <button 
              disabled={currentStep === 1} 
              onClick={() => setCurrentStep(prev => prev - 1)} 
              className="px-4 py-2 bg-base-700 hover:bg-base-600 rounded-md text-sm disabled:opacity-20 transition-colors"
            >
              Back
            </button>
            {currentStep < 4 ? (
              <button 
                onClick={() => setCurrentStep(prev => prev + 1)} 
                className="px-4 py-2 bg-primary hover:bg-primary-hover rounded-md text-sm transition-all"
              >
                Next Step
              </button>
            ) : (
              <button 
                onClick={handleFinalSave} 
                className="px-4 py-2 bg-accent hover:bg-accent-hover text-base-900 font-bold rounded-md text-sm transition-all shadow-lg shadow-accent/20"
              >
                {projectId ? 'Update Project' : 'Launch Project'}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 relative px-12">
          <div className="absolute top-6 left-12 right-12 h-0.5 bg-base-700 -z-10"></div>
          {STEPS.map(step => (
            <div key={step.id} className="flex-1 flex flex-col items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${currentStep === step.id ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/20' : currentStep > step.id ? 'bg-green-500 text-white' : 'bg-base-700 text-gray-500'}`}>
                <SafeIcon name={step.id < currentStep ? 'Check' : step.icon} className="w-5 h-5" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-hidden p-6">
        {currentStep === 1 && <Step1Tasks />}
        {currentStep === 2 && <Step2Workflows />}
        {currentStep === 3 && (
          <Step3Projects project={tempProject} setProject={setTempProject} currentProjectWfs={tempWfs} setCurrentProjectWfs={setTempWfs} />
        )}
        {currentStep === 4 && (
          <Step4Review projectData={tempProject} workflows={tempWfs} />
        )}
      </div>
    </motion.div>
  );
};